// src/services/auth.ts 完整檔案
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  GoogleAuthProvider, 
  signInWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile, 
  updatePassword, 
  EmailAuthProvider, 
  reauthenticateWithCredential,
  AuthError 
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, DocumentData } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../config/firebase';

import {
  UserOnboardingUpdateData,
  UserBackgroundUpdateData,
  UserProfileUpdateData,
  UserTimeZoneUpdateData,
  UserLanguageUpdateData,
  UserIdealWeekUpdateData,
  UserBackground,
  IdealWeek,
  OnboardingInfo
} from '../types/user';
import { logDev, logError } from '../utils/logger';

// Firebase Auth 錯誤檢查工具函數
const isAuthError = (error: unknown): error is AuthError => {
  return error instanceof Error && 'code' in error;
};

// 添加緩存變數
let cachedCurrentUser: User | null = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5000; // 5秒緩存時間
/**
 * 從 localStorage 讀取快取的 onboarding 狀態
 * 用於離線 fallback，確保已完成 Welcome/Personalization 的用戶不會重複看到
 *
 * @param userId - 用戶 ID
 * @returns onboarding 狀態或 null（快取不存在/過期/不匹配）
 */
const getCachedOnboardingStatus = (userId: string): {
  welcome?: boolean;
  personalization?: boolean;
} | null => {
  try {
    const cached = localStorage.getItem('orlo_cached_user');
    if (!cached) return null;

    const parsed = JSON.parse(cached);

    // 檢查 userId 匹配（防止跨用戶污染）
    if (parsed.uid !== userId) {
      logDev('Auth', 'localStorage 快取 userId 不匹配，忽略', {
        cachedUid: parsed.uid,
        requestedUid: userId
      });
      return null;
    }

    // 檢查是否有 onboarding 資料
    if (!parsed.onboarding) {
      logDev('Auth', 'localStorage 快取無 onboarding 資料');
      return null;
    }

    // 檢查 onboarding 快取有效期（7 天）
    const maxAge = 7 * 24 * 60 * 60 * 1000;
    if (Date.now() - parsed.onboarding.cachedAt > maxAge) {
      logDev('Auth', 'localStorage onboarding 快取已過期', {
        cachedAt: new Date(parsed.onboarding.cachedAt).toISOString(),
        ageInDays: Math.floor((Date.now() - parsed.onboarding.cachedAt) / (24 * 60 * 60 * 1000))
      });
      return null;
    }

    logDev('Auth', 'localStorage onboarding 快取有效', {
      welcome: parsed.onboarding.welcome,
      personalization: parsed.onboarding.personalization
    });

    return {
      welcome: parsed.onboarding.welcome,
      personalization: parsed.onboarding.personalization
    };
  } catch (error) {
    logDev('Auth', 'localStorage 快取讀取失敗', error);
    return null;
  }
};

/**
 * 更新 localStorage 中的 onboarding 狀態
 * 用於同步寫入，確保 Firestore 更新時 localStorage 也同步
 *
 * @param userId - 用戶 ID
 * @param update - 要更新的 onboarding 狀態（welcome 和/或 personalization）
 */
const updateLocalStorageOnboarding = (
  userId: string,
  update: { welcome?: boolean; personalization?: boolean }
): void => {
  try {
    const cached = localStorage.getItem('orlo_cached_user');
    if (!cached) {
      logDev('LocalStorage', '無快取，跳過 onboarding 更新');
      return;
    }

    const parsed = JSON.parse(cached);

    // userId 不匹配，不更新（防止跨用戶污染）
    if (parsed.uid !== userId) {
      logDev('LocalStorage', 'userId 不匹配，跳過 onboarding 更新', {
        cachedUid: parsed.uid,
        requestedUid: userId
      });
      return;
    }

    // 合併更新 onboarding 狀態
    parsed.onboarding = {
      welcome: update.welcome ?? parsed.onboarding?.welcome ?? false,
      personalization: update.personalization ?? parsed.onboarding?.personalization ?? false,
      cachedAt: Date.now()
    };

    localStorage.setItem('orlo_cached_user', JSON.stringify(parsed));
    logDev('LocalStorage', 'Onboarding 狀態已更新', {
      welcome: parsed.onboarding.welcome,
      personalization: parsed.onboarding.personalization
    });
  } catch (error) {
    // localStorage 更新失敗不影響主流程
    logDev('LocalStorage', '更新 onboarding 狀態失敗', error);
  }
};

export interface User {
  uid: string;
  email: string | null;
  displayName?: string | null;
  photoURL?: string | null;
  emailVerified: boolean;
}

// 類型安全的 Firestore 數據轉換函數
/**
 * 將自定義類型安全地轉換為 Firestore 可接受的格式
 * 這個函數確保類型安全，同時避免使用 as any
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toFirestoreData = <T extends Record<string, any>>(data: T): Partial<DocumentData> => {
  const result: Partial<DocumentData> = {};
  
  // 遍歷所有屬性，確保正確轉換
  Object.keys(data).forEach(key => {
    const value = data[key];
    
    // 處理 Date 對象 - Firestore 會自動轉換為 Timestamp
    if (value instanceof Date) {
      result[key] = value;
    }
    // 處理其他值
    else if (value !== undefined) {
      result[key] = value;
    }
  });
  
  return result;
};

// 將 Firebase User 轉換為應用程式的 User 類型
const mapFirebaseUser = (firebaseUser: FirebaseUser): User => ({
  uid: firebaseUser.uid,
  email: firebaseUser.email,
  displayName: firebaseUser.displayName,
  photoURL: firebaseUser.photoURL,
  emailVerified: firebaseUser.emailVerified
});

// 環境檢測，用於決定是否略過電子郵件驗證（在本地開發環境中）
const isDevelopment = process.env.NODE_ENV === 'development';

// 當用戶狀態變化時清除緩存
export const clearUserCache = () => {
  cachedCurrentUser = null;
  lastFetchTime = 0;
};

// 獲取用戶資料
export const getUserData = async (userId: string) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      return userDoc.data();
    }
    return null;
  } catch (error) {
    logError('Auth', '獲取用戶資料失敗', error);
    throw error;
  }
};


// 用戶文檔確保結果類型 (簡化: 移除未使用欄位)
interface EnsureUserResult {
  isNewUser: boolean;
  subscriptionChecked: boolean;
}

// 確保用戶在 Firestore 中有數據記錄 (v5.0 簡化: 移除快取機制)
export const ensureUserDocument = async (user: User): Promise<EnsureUserResult> => {
  if (!user) {
    return { isNewUser: false, subscriptionChecked: false };
  }
  
  try {
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      // 新用戶 - 創建基本文檔並初始化訂閱
      const now = new Date();
      
      await setDoc(userDocRef, {
        email: user.email,
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        createdAt: now,
        updatedAt: now,
        settings: {
          theme: 'system',
          language: navigator.language || 'en-US',
          notifications: true
        },
        // 新用戶初始化 onboarding 狀態
        onboarding: {
          welcome: false,        // 需要顯示 welcome
          personalization: false // 需要顯示 setup
        }
      });
      
      // 新用戶必須初始化訂閱
      await initializeUserSubscription();
      
      return { 
        isNewUser: true, 
        subscriptionChecked: true
      };
    } else {
      // 既有用戶 - 總是檢查訂閱狀態（包含過期檢查）
      await initializeUserSubscription();
      
      return {
        isNewUser: false,
        subscriptionChecked: true
      };
    }
  } catch (error) {
    logError('Auth', '確保用戶文檔存在時發生錯誤', error);
    return { isNewUser: false, subscriptionChecked: false };
  }
};

// 請求合併機制 - 防止並發呼叫
let pendingInitialization: Promise<void> | null = null;

// 調用 Cloud Function 初始化用戶訂閱（支援請求合併）
export const initializeUserSubscription = async (): Promise<void> => {
  // 如果已有進行中的請求，直接返回
  if (pendingInitialization) {
    return pendingInitialization;
  }
  
  // 創建新請求並記錄
  pendingInitialization = performInitialization();
  
  // 請求完成後延遲清除（避免極短時間重複）
  pendingInitialization.finally(() => {
    setTimeout(() => {
      pendingInitialization = null;
    }, 100);
  });
  
  return pendingInitialization;
};

// 實際的初始化邏輯
const performInitialization = async (): Promise<void> => {
  try {
    // 等待 Firebase Auth 從本地 IndexedDB 恢復 session（不需要網路）
    await auth.authStateReady();

    // 確保用戶已完全認證且 token 可用
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user found');
    }
    
    // 強制刷新 token 確保有效
    await currentUser.getIdToken(true);
    
    const functions = getFunctions();
    const initializeSubscription = httpsCallable(functions, 'initializeUserSubscription');
    
    const result = await initializeSubscription();
    const data = result.data as { success: boolean; message: string };
    
    if (data.success) {
      logDev('Auth', '訂閱初始化成功', { message: data.message });
    } else {
      logDev('Auth', '訂閱初始化警告', { message: data.message });
    }
  } catch (error) {
    logError('Auth', '調用訂閱初始化 Cloud Function 失敗', error);
    // 不拋出錯誤，避免阻擋用戶登入流程
  }
};

// 更新用戶個人資料
export const updateUserProfile = async (
  userId: string, 
  data: Pick<UserProfileUpdateData, 'displayName' | 'photoURL'>
) => {
  try {
    // 檢查 currentUser
    if (!auth.currentUser) {
      throw new Error('未找到已驗證的用戶');
    }

    // 1. 更新 Auth Profile
    const authUpdateData = {
      displayName: data.displayName || null
    };
    
    // 只有當明確設置了 photoURL 時才包含它
    if ('photoURL' in data) {
      // @ts-ignore - 這裡我們明確處理了類型
      authUpdateData.photoURL = data.photoURL || null;
    }
    
    await updateProfile(auth.currentUser, authUpdateData);
    
    // 2. 更新 Firestore 資料
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    const firestoreData: UserProfileUpdateData = {
      ...data,
      updatedAt: new Date()
    };
    
    if (userDoc.exists()) {
      // 使用類型安全的轉換
      await updateDoc(userDocRef, toFirestoreData(firestoreData));
    } else {
      // 如果文檔不存在，創建一個新文檔
      const userEmail = auth.currentUser.email || '';  // 安全地取得 email
      
      await setDoc(userDocRef, {
        email: userEmail,
        displayName: data.displayName || '',
        photoURL: data.photoURL || '',
        createdAt: new Date(),
        updatedAt: new Date(),
        settings: {
          theme: 'system',
          language: navigator.language || 'en-US',
          notifications: true
        }
      });
    }
    
    return true;
  } catch (error) {
    logError('Auth', '更新用戶個人資料失敗', error);
    throw error;
  }
};

// 更改密碼 (需要重新驗證)
export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error('No authenticated user or email');
    }
    
    // 重新驗證用戶
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    
    // 更新密碼
    await updatePassword(user, newPassword);
    
    return;
  } catch (error: unknown) {
    logError('Auth', '更改密碼失敗', error);
    
    // 處理特定錯誤碼
    if (isAuthError(error)) {
      if (error.code === 'auth/wrong-password') {
        throw new Error('當前密碼不正確');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('新密碼太弱，請使用至少6個字符');
      }
    }
    
    throw error;
  }
};

// 註冊新用戶（電子郵件/密碼）
export const registerWithEmail = async (email: string, password: string): Promise<User> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // 發送電子郵件驗證
    if (!isDevelopment) {
      await sendEmailVerification(userCredential.user);
    } else {
    }
    
    // 在 Firestore 中創建用戶資料
    await createUserInFirestore(userCredential.user);
    
    return mapFirebaseUser(userCredential.user);
  } catch (error: unknown) {
    logError('Auth', '註冊失敗', error);
    throw error;
  }
};

// 使用電子郵件/密碼登入
export const loginWithEmail = async (email: string, password: string): Promise<User> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return mapFirebaseUser(userCredential.user);
  } catch (error: unknown) {
    logError('Auth', '登入失敗', error);
    throw error;
  }
};

// 使用 Google 登入
export const loginWithGoogle = async (): Promise<User> => {
  try {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    
    // 檢查用戶是否已存在於 Firestore，若不存在則創建
    await createUserInFirestore(userCredential.user);
    
    return mapFirebaseUser(userCredential.user);
  } catch (error: unknown) {
    logError('Auth', 'Google 登入失敗', error);
    throw error;
  }
};

// 登出
export const logout = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error: unknown) {
    logError('Auth', '登出失敗', error);
    throw error;
  }
};

// 發送密碼重設郵件
export const sendPasswordReset = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: unknown) {
    logError('Auth', '發送密碼重設郵件失敗', error);
    throw error;
  }
};

// 檢查用戶是否已登入
export const isAuthenticated = (): boolean => {
  const isAuth = auth.currentUser !== null;
  return isAuth;
};

// 獲取當前用戶（帶緩存）
export const getCurrentUser = (): User | null => {
  const now = Date.now();

  // 如果緩存有效期內，直接返回緩存的用戶
  if (cachedCurrentUser && (now - lastFetchTime) < CACHE_DURATION) {
    return cachedCurrentUser;
  }

  const firebaseUser = auth.currentUser;
  if (firebaseUser) {
    // 更新緩存
    cachedCurrentUser = mapFirebaseUser(firebaseUser);
    lastFetchTime = now;

    return cachedCurrentUser;
  }

  // 用戶未登入，清除緩存
  cachedCurrentUser = null;
  lastFetchTime = now;
  return null;
};

// 獲取當前用戶 ID
// Firebase SDK 初始化前 auth.currentUser 為 null，
// fallback 到 localStorage cache 確保首次 render 就能取得正確 userId
export const getCurrentUserId = (): string => {
  const user = getCurrentUser();
  if (user) return user.uid;

  // Fallback: Firebase SDK 尚未初始化時，從 localStorage cache 讀取
  try {
    const cached = localStorage.getItem('orlo_cached_user');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed?.uid) return parsed.uid;
    }
  } catch {
    // localStorage 讀取失敗，回傳空字串
  }

  return "";
};

// 創建用戶在 Firestore 中的資料
// 導出這個函數以便可以在測試中直接使用
export const createUserInFirestore = async (firebaseUser: FirebaseUser): Promise<void> => {
  try {
    const userDocRef = doc(db, 'users', firebaseUser.uid);
    
    const userDoc = await getDoc(userDocRef);
    
    // 如果用戶資料不存在，則創建
    if (!userDoc.exists()) {
      
      // 獲取瀏覽器時區
      const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      const userData = {
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || '',
        photoURL: firebaseUser.photoURL || '',
        createdAt: new Date(),
        updatedAt: new Date(),
        timeZone: browserTimeZone, // 儲存用戶時區
        settings: {
          theme: 'system',
          language: navigator.language || 'en-US',
          notifications: true
        }
      };
      
      await setDoc(userDocRef, userData);
    } else {
    }
  } catch (error) {
    logError('Auth', '創建用戶資料失敗', error);
    throw error;
  }
};

// 監聽用戶身份狀態變化（包含緩存清除）
export const subscribeToAuthChanges = (callback: (user: User | null) => void): (() => void) => {
  return onAuthStateChanged(auth, (firebaseUser) => {
    // 清除緩存
    clearUserCache();
    
    if (firebaseUser) {
      // 在本地開發環境中，如果使用模擬器，跳過郵件驗證檢查
      if (isDevelopment && !firebaseUser.emailVerified) {
        const user = mapFirebaseUser(firebaseUser);
        user.emailVerified = true;
        callback(user);
      } else {
        callback(mapFirebaseUser(firebaseUser));
      }
    } else {
      callback(null);
    }
  });
};

/**
 * 更新用戶時區設置
 * @param userId 用戶ID
 * @param timeZone 時區字符串（例如 'Asia/Taipei'）
 * @returns 成功返回 true，失敗返回 false
 */
export const updateUserTimeZone = async (userId: string, timeZone: string): Promise<boolean> => {
  try {
    // 檢查時區是否有效
    try {
      Intl.DateTimeFormat(undefined, { timeZone });
    } catch (e) {
      logError('Auth', 'Invalid timezone', { timeZone });
      return false;
    }
    
    const userDocRef = doc(db, 'users', userId);
    
    // 使用強類型的更新數據
    const updateData: UserTimeZoneUpdateData = {
      timeZone,
      updatedAt: new Date()
    };
    
    // 使用類型安全的轉換
    await updateDoc(userDocRef, toFirestoreData(updateData));

    return true;
  } catch (error) {
    logError('Auth', '更新用戶時區失敗', error);
    return false;
  }
};

/**
 * 更新用戶語言偏好設定
 * @param userId 用戶ID
 * @param language 語言代碼（例如 'en-US', 'zh-TW'）
 * @returns 成功返回 true，失敗拋出錯誤
 */
export const updateUserLanguage = async (userId: string, language: string): Promise<boolean> => {
  
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      throw new Error('用戶資料不存在');
    }
    
    const userData = userDoc.data();
    
    // 內建遷移邏輯：確保 settings 結構完整
    const currentSettings = userData.settings || {};
    const updatedSettings = {
      theme: 'system',
      notifications: true,
      ...currentSettings,  // 保留現有設定
      language  // 更新語言
    };
    
    // 使用強類型的更新數據
    const updateData: UserLanguageUpdateData = {
      settings: updatedSettings,
      updatedAt: new Date()
    };
    
    // 執行更新
    await updateDoc(userDocRef, toFirestoreData(updateData));
    
    return true;
  } catch (error) {
    logError('Auth', '更新用戶語言偏好失敗', error);
    throw error;
  }
};


/**
 * 更新用戶背景資訊
 * @param userId 用戶ID
 * @param backgroundData 包含職業、工作型態、工作時間等的背景資訊
 * @returns 成功返回 true，失敗拋出錯誤
 */
export const updateUserBackground = async (
  userId: string, 
  backgroundData: UserBackground
): Promise<boolean> => {
  try {
    // 檢查用戶文檔是否存在
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      throw new Error('用戶資料不存在');
    }
    
    // 使用強類型的更新數據
    const updateData: UserBackgroundUpdateData = {
      background: backgroundData,
      updatedAt: new Date()
    };
    
    // 使用類型安全的轉換
    await updateDoc(userDocRef, toFirestoreData(updateData));

    return true;
  } catch (error) {
    logError('Auth', '更新用戶背景資訊失敗', error);
    throw error;
  }
};

export { updateUserIdealWeek } from '../features/idealWeek/services/idealWeekService';

/**
 * 檢查用戶是否需要個人化設置
 * 簡化版本：只依賴 Firestore，移除 localStorage 邏輯
 * @param userId 用戶ID
 * @returns true: 需要個人化設置，false: 不需要設置
 */
export const shouldShowPersonalization = async (userId: string): Promise<boolean> => {
  try {
    logDev('Auth', '檢查個人化設置狀態', { userId });

    // 直接查詢 Firestore 狀態
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      logDev('Auth', '用戶文檔不存在，需要設置', { userId });
      return true; // 用戶不存在，需要設置
    }

    const userData = userDoc.data();
    if (!userData) {
      logDev('Auth', '用戶數據為空，需要設置', { userId });
      return true;
    }

    // 獲取 onboarding 狀態
    const onboarding = (userData.onboarding as OnboardingInfo) || {};
    const personalizationCompleted = onboarding.personalization === true;

    logDev('Auth', '個人化設置檢查完成', {
      userId,
      personalizationCompleted,
      needsSetup: !personalizationCompleted
    });

    // 只有明確完成個人化設置的用戶才跳過
    return personalizationCompleted !== true;

  } catch (error) {
    logError('Auth', '檢查個人化設置需求失敗，採用保守策略', error);

    // 嘗試從 localStorage 讀取快取作為 fallback
    const cached = getCachedOnboardingStatus(userId);
    if (cached && cached.personalization === true) {
      logDev('Auth', '網路失敗，使用 localStorage 快取（已完成 Personalization）', { userId });
      return false; // 快取顯示已完成，不需要設置
    }

    // 沒有快取或快取顯示未完成，保守處理
    logDev('Auth', '檢查失敗且無有效快取，默認需要設置', {
      userId,
      error: error instanceof Error ? error.message : String(error),
      hasCache: !!cached,
      cachePersonalization: cached?.personalization
    });
    return true; // 保守處理：錯誤時默認需要設置
  }
};

/**
 * 獲取用戶完整的 onboarding 狀態
 * @param userId 用戶ID
 * @returns onboarding 狀態或 null
 */
export const getUserOnboardingStatus = async (userId: string): Promise<OnboardingInfo | null> => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      return null;
    }

    const userData = userDoc.data();
    const onboarding = userData.onboarding as OnboardingInfo || {};

    return onboarding;

  } catch (error) {
    logError('Auth', '獲取 onboarding 狀態失敗', error);
    return null;
  }
};

/**
 * 呼叫後端 API 處理個人化設置流程
 * @param answers 三個問題的答案
 * @returns 處理結果
 */
export const processPersonalizationFlow = async (answers: {
  question1?: string;
  question2?: string;
  question3?: string;
}): Promise<{
  success: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: Record<string, any>;
  error?: string;
}> => {
  try {
    const functions = getFunctions();
    const processFlow = httpsCallable(functions, 'processPersonalizationFlow');

    const result = await processFlow({ answers });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return result.data as { success: boolean; data?: Record<string, any>; error?: string };

  } catch (error) {
    logError('Auth', '處理個人化設置失敗', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '未知錯誤'
    };
  }
};

/**
 * 創建用戶引導種子資料（Timeboxes）
 *
 * 觸發時機：
 * - SetupIntro (Step 0) 跳過
 * - SetupStep3 (Step 3) 完成/跳過
 *
 * @param params 參數物件
 * @param params.timeZone 用戶時區（例如: 'Asia/Taipei'）
 * @param params.locale 語言代碼（可選，預設 'en'）
 * @returns 處理結果
 */
export const createOnboardingData = async (params: {
  timeZone: string;
  locale?: string;
}): Promise<{
  success: boolean;
  timeboxIds?: string[];
  captureIds?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: Record<string, any>;
  alreadyGenerated?: boolean;
  error?: string;
}> => {
  try {
    // 等待 Firebase Auth 從本地 IndexedDB 恢復 session（不需要網路）
    await auth.authStateReady();

    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('用戶未登入');
    }

    // 前端優化：檢查是否已生成過 seed data（避免不必要的 API 調用）
    const userId = currentUser.uid;
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (userData?.onboarding?.seedDataGenerated) {
        logDev('CreateOnboardingData', '前端檢測到 Seed data 已存在，跳過 API 調用', {
          userId,
          existingVersion: userData.onboarding.seedDataVersion
        });

        return {
          success: true,
          alreadyGenerated: true,
          metadata: {
            version: userData.onboarding.seedDataVersion,
            generatedAt: userData.onboarding.seedDataGeneratedAt?.toDate?.()?.toISOString(),
            timeZone: params.timeZone,
            locale: params.locale || 'en'
          }
        };
      }
    }

    // 如果未生成，呼叫後端 Cloud Function
    logDev('CreateOnboardingData', '前端檢測到 Seed data 未生成，呼叫後端創建', { userId });

    const functions = getFunctions();
    const createOnboarding = httpsCallable(functions, 'createOnboardingData');

    const result = await createOnboarding(params);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return result.data as { success: boolean; timeboxIds?: string[]; captureIds?: string[]; metadata?: Record<string, any>; alreadyGenerated?: boolean; error?: string };

  } catch (error) {
    logError('Auth', '創建 Onboarding 資料失敗', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '未知錯誤'
    };
  }
};

/**
 * 標記個人化設置狀態（簡化版本）
 * 只更新 Firestore，移除 localStorage 邏輯
 * @param completed 是否標記為完成
 * @returns 標記結果
 */
export const markPersonalizationStatus = async (completed: boolean = false): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    // 等待 Firebase Auth 從本地 IndexedDB 恢復 session（不需要網路）
    await auth.authStateReady();

    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('用戶未登入');
    }

    const userId = currentUser.uid;
    logDev('Auth', '標記個人化設置狀態', { userId, completed });

    // 同步更新 localStorage
    updateLocalStorageOnboarding(userId, { personalization: completed });

    // 直接更新 Firestore 中的 onboarding 狀態
    const userDocRef = doc(db, 'users', userId);

    // 先讀取現有的 onboarding 狀態，避免覆蓋 welcome 設定
    const userDoc = await getDoc(userDocRef);
    const currentOnboarding = userDoc.exists() ? userDoc.data()?.onboarding || {} : {};

    const updateData: UserOnboardingUpdateData = {
      onboarding: {
        welcome: currentOnboarding.welcome || false, // 保持現有 welcome 狀態
        personalization: completed
      },
      updatedAt: new Date()
    };

    await updateDoc(userDocRef, toFirestoreData(updateData));

    logDev('Auth', '個人化設置狀態更新成功', { userId, completed });
    return { success: true };

  } catch (error) {
    logError('Auth', '標記個人化設置狀態失敗', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '未知錯誤'
    };
  }
};